/**
 * m3 echo Frontend - Password Change Dialog
 *
 * Dialog for changing user password (both voluntary and forced).
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Box,
  CircularProgress,
} from '@mui/material';
import { authService } from '../services/auth';
import { useAuthStore, useMustChangePassword, useToken } from '../stores/useAuthStore';

interface PasswordChangeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  isRequired?: boolean;
}

export const PasswordChangeDialog: React.FC<PasswordChangeDialogProps> = ({
  isOpen,
  onClose,
  isRequired = false,
}) => {
  const token = useToken();
  const { updateMustChangePassword } = useAuthStore();
  const mustChangePassword = useMustChangePassword();

  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validatePassword = (password: string): string | null => {
    if (password.length < 8) {
      return 'Passwort muss mindestens 8 Zeichen lang sein';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Passwort muss mindestens 1 Großbuchstaben enthalten';
    }
    if (!/[a-z]/.test(password)) {
      return 'Passwort muss mindestens 1 Kleinbuchstaben enthalten';
    }
    if (!/\d/.test(password)) {
      return 'Passwort muss mindestens 1 Zahl enthalten';
    }
    const specialChars = '!@#$%^&*()_+-=[]{}|;:,.<>?';
    if (!specialChars.split('').some(char => password.includes(char))) {
      return 'Passwort muss mindestens 1 Sonderzeichen enthalten (!@#$%^&*()_+-=[]{}|;:,.<>?)';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    // Validate new password
    const validationError = validatePassword(newPassword);
    if (validationError) {
      setError(validationError);
      return;
    }

    // Check passwords match
    if (newPassword !== confirmPassword) {
      setError('Passwörter stimmen nicht überein');
      return;
    }

    // Check new password is different from old
    if (oldPassword === newPassword) {
      setError('Neues Passwort muss sich vom alten unterscheiden');
      return;
    }

    setIsSubmitting(true);

    try {
      await authService.changePassword(token!, {
        old_password: oldPassword,
        new_password: newPassword,
      });

      setSuccess(true);
      updateMustChangePassword(false);

      // Clear form
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');

      // Close dialog after short delay if not required
      if (!isRequired) {
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        // If required, close immediately to allow navigation
        setTimeout(() => {
          onClose();
          window.location.href = '/'; // Force reload to refresh state
        }, 1500);
      }
    } catch (err: any) {
      console.error('Password change error:', err);
      setError(err.message || 'Passwort konnte nicht geändert werden. Bitte versuchen Sie es erneut.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isRequired && !mustChangePassword) {
      setError('');
      setSuccess(false);
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      onClose();
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={isRequired || mustChangePassword}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {isRequired || mustChangePassword
            ? 'Passwortänderung erforderlich'
            : 'Passwort ändern'}
        </DialogTitle>

        <DialogContent>
          {(isRequired || mustChangePassword) && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Sie müssen Ihr Passwort ändern, bevor Sie fortfahren können.
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Passwort erfolgreich geändert!
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Aktuelles Passwort"
              type="password"
              required
              fullWidth
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              disabled={isSubmitting || success}
            />

            <TextField
              label="Neues Passwort"
              type="password"
              required
              fullWidth
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={isSubmitting || success}
              helperText="Mind. 8 Zeichen, 1 Großbuchstabe, 1 Kleinbuchstabe, 1 Zahl, 1 Sonderzeichen"
            />

            <TextField
              label="Neues Passwort bestätigen"
              type="password"
              required
              fullWidth
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isSubmitting || success}
            />
          </Box>
        </DialogContent>

        <DialogActions>
          {!isRequired && !mustChangePassword && (
            <Button
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Abbrechen
            </Button>
          )}
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting || success}
            startIcon={isSubmitting && <CircularProgress size={20} />}
          >
            {isSubmitting ? 'Wird geändert...' : 'Passwort ändern'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
